import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { normalizeModeCompteur } from './normalize';

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return String(n);
};

const formatWithUnit = (value, mode) => {
  const v = formatValue(value);
  if (v === '—') return v;
  return `${v} ${normalizeModeCompteur(mode) === 'km' ? 'km' : 'h'}`;
};

const safeText = (value) => {
  return String(value ?? '')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/\u2026/g, '...')
    .replace(/\u00A0/g, ' ');
};

export const generateVidangePDF = ({
  title = 'Suivi Vidange',
  userName = '',
  date = '',
  rows = [],
  filters = {}
}) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const pageWidth = doc.internal.pageSize.getWidth();

  // 🎨 Couleurs thème Shems
  const COLOR_PRIMARY = [109, 78, 54];
  const COLOR_SECONDARY = [139, 94, 60];
  const COLOR_LIGHT = [245, 245, 219];
  const COLOR_TEXT = [60, 40, 30];

  // 🧾 En-tête
  doc.setFillColor(...COLOR_LIGHT);
  doc.rect(0, 0, pageWidth, 28, 'F');

  doc.setTextColor(...COLOR_PRIMARY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(safeText(title), 14, 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_TEXT);

  const headerLines = [
    `Utilisateur : ${safeText(userName || '—')}`,
    `Date génération : ${safeText(date || '—')}`
  ];

  headerLines.forEach((line, idx) => {
    doc.text(line, 14, 18 + idx * 5);
  });

  // 🔎 Filtres
  const filterText = [
    `Chantier : ${safeText(filters?.chantier || 'Tous')}`,
    `Société : ${safeText(filters?.societe || 'Toutes')}`,
    `Statut : ${safeText(filters?.statut || 'Tous')}`,
    `Mode : ${safeText(filters?.mode_compteur || 'Tous')}`
  ];

  doc.setFontSize(8);
  filterText.forEach((line, idx) => {
    doc.text(line, 150, 14 + idx * 4);
  });

  // 📊 Lignes
  const body = (rows || []).map((row) => {
    const mode = normalizeModeCompteur(row?.mode_compteur);
    const modeLabel = mode === 'km' ? 'KM' : 'Heure';

    const cycle =
      row?.cycle_value !== undefined && row?.cycle_value !== null
        ? row.cycle_value
        : mode === 'km'
          ? row?.compteur_vidange_km
          : row?.compteur_vidange_h;

    const intervale =
      row?.interval_value !== undefined && row?.interval_value !== null
        ? row.interval_value
        : mode === 'km'
          ? row?.interval_vidange_km
          : row?.interval_vidange_h;

    const reste =
      row?.reste_value !== undefined && row?.reste_value !== null
        ? row.reste_value
        : mode === 'km'
          ? row?.reste_km
          : row?.reste_h;

    return [
      safeText(row?.id ?? '—'),
      safeText(row?.matricule || '—'),
      safeText(row?.type || '—'),
      safeText(row?.societe || '—'),
      safeText(modeLabel),
      safeText(row?.chantier_actuel || '—'),
      safeText(row?.date_vidange || '—'),
      safeText(formatWithUnit(cycle, mode)),
      safeText(formatWithUnit(intervale, mode)),
      safeText(formatWithUnit(reste, mode)),
      safeText(row?.statut || '—')
    ];
  });

  autoTable(doc, {
    startY: 32,
    head: [[
      'ID',
      'Matricule',
      'Type',
      'Société',
      'Mode',
      'Chantier',
      'Date vidange',
      'Cycle actuel',
      'Intervalle',
      'Reste',
      'Statut'
    ]],
    body,
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 2,
      textColor: COLOR_TEXT,
      lineColor: [220, 220, 220],
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: COLOR_PRIMARY,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle'
    },
    bodyStyles: {
      valign: 'middle'
    },
    alternateRowStyles: {
      fillColor: [252, 250, 244]
    },
    columnStyles: {
      0: { cellWidth: 12, halign: 'center' },
      1: { cellWidth: 28 },
      2: { cellWidth: 28 },
      3: { cellWidth: 24 },
      4: { cellWidth: 16, halign: 'center' },
      5: { cellWidth: 38 },
      6: { cellWidth: 22, halign: 'center' },
      7: { cellWidth: 24, halign: 'right' },
      8: { cellWidth: 24, halign: 'right' },
      9: { cellWidth: 22, halign: 'right' },
      10: { cellWidth: 20, halign: 'center' }
    },
    didParseCell: (data) => {
      if (data.section !== 'body') return;

      const statut = String(data.row.raw?.[10] || '').toUpperCase();

      if (data.column.index === 10) {
        if (statut.includes('DEPASSE')) {
          data.cell.styles.fillColor = [70, 70, 70];
          data.cell.styles.textColor = [255, 255, 255];
        } else if (statut.includes('CRITIQUE')) {
          data.cell.styles.fillColor = [220, 53, 69];
          data.cell.styles.textColor = [255, 255, 255];
        } else if (statut.includes('ALERTE')) {
          data.cell.styles.fillColor = [255, 193, 7];
          data.cell.styles.textColor = [40, 40, 40];
        } else if (statut.includes('OK')) {
          data.cell.styles.fillColor = [25, 135, 84];
          data.cell.styles.textColor = [255, 255, 255];
        }
      }

      const mode = String(data.row.raw?.[4] || '').toUpperCase();
      if (data.column.index === 4) {
        if (mode === 'KM') {
          data.cell.styles.fillColor = [13, 110, 253];
          data.cell.styles.textColor = [255, 255, 255];
        } else {
          data.cell.styles.fillColor = [13, 202, 240];
          data.cell.styles.textColor = [40, 40, 40];
        }
      }
    },
    didDrawPage: () => {
      const pageCount = doc.internal.getNumberOfPages();
      const pageSize = doc.internal.pageSize;
      const pageHeight = pageSize.getHeight();
      const pageWidthInner = pageSize.getWidth();

      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `PaieShems • Suivi vidange • Page ${pageCount}`,
        pageWidthInner - 70,
        pageHeight - 6
      );
    }
  });

  const safeTitle = safeText(title)
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^\w\-]+/g, '');

  const fileName = `${safeTitle || 'suivi_vidange'}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(fileName);
};

export default generateVidangePDF;